import { Box, Card, Flex, Icon, SimpleGrid, Stack, Text } from '@chakra-ui/react';
import type { ComponentType, SVGProps } from 'react';
import {
  PlusIcon,
  PencilIcon,
  BookOpenIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import { PlanScope } from '@/lib/types';
import { PlannerAction } from '@/pages/planner/planner-machine';
import { blankDraft } from '@/pages/planner/draft';
import DatePill from './date-pill';

interface ChoiceScreenProps {
  date: string;
  // Which of the date's slots is being filled — the shared workspace plan or
  // the caller's private "My Plan". Preset by the scope tab strip and carried
  // into every creation flow so the plan saves into the right slot.
  scope: PlanScope;
  workspaceId: number;
  dispatch: (action: PlannerAction) => void;
}

interface ChoiceCardProps {
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  iconBg: string;
  iconFg: string;
  title: string;
  description: string;
  // Brand-outlined card with a "Most used" tab riding its top border.
  featured?: boolean;
  onClick: () => void;
}

function ChoiceCard({ icon, iconBg, iconFg, title, description, featured, onClick }: ChoiceCardProps) {
  return (
    <Card.Root
      position='relative'
      transition='all 0.18s ease'
      shadow='sm'
      borderColor={featured ? 'brand.solid' : undefined}
      _hover={{
        shadow: 'md',
        transform: 'translateY(-2px)',
        borderColor: featured ? 'brand.solid' : 'border.emphasized',
      }}
    >
      {featured && (
        <Box
          position='absolute'
          top='0'
          left={4}
          transform='translateY(-50%)'
          bg='brand.solid'
          color='brand.contrast'
          borderRadius='full'
          px={3}
          py={0.5}
          fontFamily='heading'
          fontWeight='700'
          fontSize='label'
          textTransform='uppercase'
          letterSpacing='label'
          whiteSpace='nowrap'
        >
          Most used
        </Box>
      )}
      <Box as='button' type='button' onClick={onClick} textAlign='left' width='100%' height='100%' cursor='pointer'>
        <Card.Body height='100%' display='flex' flexDirection='column'>
          <Flex
            align='center'
            justify='center'
            w={10}
            h={10}
            borderRadius='md'
            bg={iconBg}
            mb={4}
          >
            <Icon as={icon} w={5} h={5} color={iconFg} />
          </Flex>
          <Text fontSize='lg' fontWeight='700' color='fg.strong' lineHeight='short'>
            {title}
          </Text>
          <Text fontSize='sm' color='fg.muted' mt={1.5}>
            {description}
          </Text>
          <Flex justify='flex-end' mt='auto' pt={4}>
            <Icon as={ArrowRightIcon} w={4} h={4} color={featured ? 'brand.solid' : 'fg.subtle'} />
          </Flex>
        </Card.Body>
      </Box>
    </Card.Root>
  );
}

// `choice` screen — an empty day slot (workspace plan or "My Plan") is
// selected. Header row (date pill + title + hint) over three cards: create a
// new plan from scratch ("Most used"), reuse a past plan, or pull from a
// curriculum. All roles go to the add-to-date picker (curPlans); admins
// reach the manage flow (curBuild) via that screen's Manage action. Every
// path threads `scope` through to the eventual draft.
export default function ChoiceScreen({ date, scope, workspaceId, dispatch }: ChoiceScreenProps) {
  return (
    <Stack gap={5}>
      <Flex align='center' gap={3} flexWrap='wrap' pb={3} borderBottomWidth='1px' borderColor='border'>
        <DatePill date={date} />
        <Text fontSize='lg' fontWeight='700' color='fg.strong'>
          {scope === 'personal' ? 'Add your own plan for this day' : 'Add the workspace plan for this day'}
        </Text>
        <Text fontSize='sm' color='fg.muted' ml='auto'>
          {scope === 'personal'
            ? 'Only you will see this plan.'
            : 'How do you want to plan this session?'}
        </Text>
      </Flex>

      <SimpleGrid columns={{ base: 1, md: 3 }} gap={4} pt={1}>
        <ChoiceCard
          icon={PlusIcon}
          iconBg='brand.soft'
          iconFg='brand.solid'
          title='Create new plan'
          description='Build a fresh practice from scratch with custom sections and content.'
          featured
          onClick={() => dispatch({ type: 'CHOOSE_CREATE_NEW', draft: blankDraft(workspaceId, date, scope) })}
        />
        <ChoiceCard
          icon={PencilIcon}
          iconBg='slot1.bg'
          iconFg='slot1.fg'
          title='Use & modify past plan'
          description='Copy one of your previous plans and adjust it.'
          onClick={() => dispatch({ type: 'CHOOSE_MOD_PAST', date, scope })}
        />
        <ChoiceCard
          icon={BookOpenIcon}
          iconBg='slot6.bg'
          iconFg='slot6.fg'
          title='Add from Curriculum'
          description='Pull a ready-built plan from a program curriculum and adapt it.'
          onClick={() => dispatch({ type: 'CHOOSE_CURRICULUM', date, intent: 'addToDate', scope })}
        />
      </SimpleGrid>
    </Stack>
  );
}
